/**
 * App-wide "unsaved changes" navigation guard.
 *
 * A screen with editable, unsaved state registers a guard:
 *
 *   useNavigationGuard("report:123", {
 *     isDirty,                       // true while there are unsaved changes
 *     name: "Daily Sales Report",    // shown in the dialog title
 *     onSave: async () => saveView(), // resolve true to continue leaving
 *     onDiscard: () => resetDraft(),
 *   });
 *
 * In-app exits go through `useGuardedNavigate()` (a drop-in for `useNavigate`)
 * or `useConfirmLeave()` (wrap any other "leave" action, e.g. sign-out or the
 * location picker). When a registered guard is dirty, the provider shows ONE
 * shared UnsavedChangesDialog — Save / Discard changes complete the pending
 * exit; closing it (X, Esc or a click outside) cancels it. While any guard is dirty a `beforeunload` handler is
 * attached, so reload / tab close prompt the browser's native confirm.
 *
 * Known limitation: browser Back / Forward is NOT guarded. The app uses
 * <BrowserRouter> (not a data router), and react-router's `useBlocker` only
 * works with data routers, so a history pop has already happened by the time
 * React sees it. Nav rail / flyout / avatar-menu / mobile nav sheet clicks,
 * opening an alert, sign-out, the location picker / switch, and reload/close
 * are guarded. Moving to a data router would let us block history pops too.
 *
 * Every hook degrades gracefully without the provider (tests, isolated
 * renders): navigation proceeds immediately.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  NavigateOptions,
  To,
  useLocation,
  useNavigate,
} from "react-router-dom";
import UnsavedChangesDialog from "../components/common/UnsavedChangesDialog";
import { notifyError } from "../utils/notify";

export interface NavigationGuardOptions {
  /** True while there are unsaved changes. */
  isDirty: boolean;
  /** Persist the changes. Resolve `true` to continue leaving, `false` to
   *  stay (e.g. the save failed and already surfaced an error). A rejection
   *  also stays, with a generic error notification. */
  onSave: () => Promise<boolean>;
  /** Drop the unsaved changes (called before the exit proceeds). */
  onDiscard: () => void;
  /** Display name of the thing being edited, for the dialog title. */
  name: string;
  /** Optional dialog body copy override. */
  description?: React.ReactNode;
}

type GuardRef = React.MutableRefObject<NavigationGuardOptions>;

interface NavigationGuardContextValue {
  register: (id: string, guard: GuardRef) => () => void;
  setDirty: (id: string, dirty: boolean) => void;
  /** Run `proceed` now, or after the user resolves the unsaved-changes dialog. */
  confirmLeave: (proceed: () => void) => void;
  /** True while any registered guard has unsaved changes. */
  isAnyDirty: boolean;
}

const NavigationGuardContext =
  createContext<NavigationGuardContextValue | null>(null);

interface PendingLeave {
  guardId: string;
  proceed: () => void;
  /** Guards already saved / discarded during this exit attempt. */
  handled: ReadonlySet<string>;
}

export function NavigationGuardProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const guards = useRef(new Map<string, GuardRef>());
  const [dirtyIds, setDirtyIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [pending, setPending] = useState<PendingLeave | null>(null);
  const [saving, setSaving] = useState(false);

  const setDirty = useCallback((id: string, dirty: boolean) => {
    setDirtyIds((prev) => {
      if (prev.has(id) === dirty) return prev;
      const next = new Set(prev);
      if (dirty) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const register = useCallback(
    (id: string, guard: GuardRef) => {
      guards.current.set(id, guard);
      return () => {
        if (guards.current.get(id) === guard) guards.current.delete(id);
        setDirty(id, false);
        // The guarded screen is gone (e.g. browser Back unmounted it while
        // the dialog was open): drop its pending exit so the dialog can't
        // resurface later and run a stale navigation / sign-out.
        setPending((p) => (p?.guardId === id ? null : p));
      };
    },
    [setDirty],
  );

  // Walk the dirty guards one at a time (normally there is only one), then
  // run the exit once every dirty guard has been saved or discarded.
  const advance = useCallback(
    (proceed: () => void, handled: ReadonlySet<string>) => {
      const next = Array.from(guards.current.entries()).find(
        ([id, ref]) => ref.current.isDirty && !handled.has(id),
      );
      if (!next) {
        setPending(null);
        proceed();
        return;
      }
      setPending({ guardId: next[0], proceed, handled });
    },
    [],
  );

  const confirmLeave = useCallback(
    (proceed: () => void) => advance(proceed, new Set()),
    [advance],
  );

  const isAnyDirty = dirtyIds.size > 0;

  // Any route change that didn't come from the dialog (browser Back /
  // Forward, an unguarded redirect) makes a pending exit stale.
  const { pathname } = useLocation();
  useEffect(() => {
    setPending(null);
  }, [pathname]);

  // Native confirm on reload / tab close, only while something is dirty.
  useEffect(() => {
    if (!isAnyDirty) return undefined;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Legacy browsers require a returnValue to show the prompt.
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isAnyDirty]);

  const activeGuard = pending
    ? guards.current.get(pending.guardId)?.current
    : undefined;
  // Keep the last name / copy for the dialog's closing transition.
  const shownGuard = useRef<NavigationGuardOptions | undefined>(undefined);
  if (activeGuard) shownGuard.current = activeGuard;

  const markHandledAndContinue = () => {
    if (!pending) return;
    // The guard's own state update (draft saved / dropped) only lands on its
    // next render, so mark it clean now: an exit that itself navigates through
    // a guarded navigate must not prompt a second time. The guard's next render
    // overwrites this with its real isDirty.
    const ref = guards.current.get(pending.guardId);
    if (ref) ref.current = { ...ref.current, isDirty: false };
    // …and clear it from the dirty set right away, so isAnyDirty /
    // beforeunload / callers of useIsNavigationDirty reset immediately.
    setDirty(pending.guardId, false);
    const handled = new Set(pending.handled);
    handled.add(pending.guardId);
    advance(pending.proceed, handled);
  };

  const handleSave = async () => {
    if (!pending || !activeGuard) return;
    setSaving(true);
    let ok = false;
    try {
      ok = await activeGuard.onSave();
    } catch (e) {
      // onSave should surface its own errors and resolve false; this is the
      // fallback so a thrown save never fails silently.
      console.error("Saving before navigation failed:", e);
      notifyError("Your changes weren't saved. Try again.", {
        title: "Couldn't save",
      });
      ok = false;
    } finally {
      setSaving(false);
    }
    if (ok) markHandledAndContinue();
    else setPending(null);
  };

  const handleDiscard = () => {
    if (!pending || !activeGuard) return;
    activeGuard.onDiscard();
    markHandledAndContinue();
  };

  const value = useMemo(
    () => ({ register, setDirty, confirmLeave, isAnyDirty }),
    [register, setDirty, confirmLeave, isAnyDirty],
  );

  return (
    <NavigationGuardContext.Provider value={value}>
      {children}
      <UnsavedChangesDialog
        opened={!!pending && !!activeGuard}
        name={shownGuard.current?.name ?? ""}
        description={shownGuard.current?.description}
        saving={saving}
        onSave={handleSave}
        onDiscard={handleDiscard}
        onKeepEditing={() => setPending(null)}
      />
    </NavigationGuardContext.Provider>
  );
}

/**
 * Register an unsaved-changes guard for the lifetime of the calling component.
 * `id` must be unique among mounted guards (e.g. `report:${reportId}`).
 */
export function useNavigationGuard(
  id: string,
  options: NavigationGuardOptions,
): void {
  const ctx = useContext(NavigationGuardContext);
  const ref = useRef(options);
  ref.current = options;
  const register = ctx?.register;
  const setDirty = ctx?.setDirty;

  useEffect(() => {
    if (!register) return undefined;
    return register(id, ref);
  }, [register, id]);

  // Re-synced after every render (setDirty bails out when unchanged), so the
  // dirty set self-heals if a Save / Discard didn't actually clean the screen.
  const isDirty = options.isDirty;
  useEffect(() => {
    setDirty?.(id, isDirty);
  });
}

/**
 * Wrap any "leave" action (sign-out, opening the location picker, …) so it
 * first resolves unsaved changes. Without a provider it runs immediately.
 */
export function useConfirmLeave(): (proceed: () => void) => void {
  const ctx = useContext(NavigationGuardContext);
  const confirmLeave = ctx?.confirmLeave;
  return useCallback(
    (proceed: () => void) => {
      if (confirmLeave) confirmLeave(proceed);
      else proceed();
    },
    [confirmLeave],
  );
}

export type GuardedNavigate = {
  (to: To, options?: NavigateOptions): void;
  (delta: number): void;
};

/**
 * Drop-in replacement for `useNavigate()` whose navigations first resolve any
 * unsaved changes (see the module doc for what is and isn't guarded).
 */
export function useGuardedNavigate(): GuardedNavigate {
  const navigate = useNavigate();
  const confirmLeave = useConfirmLeave();
  return useCallback(
    (to: To | number, options?: NavigateOptions) => {
      confirmLeave(() => {
        if (typeof to === "number") navigate(to);
        else if (options === undefined) navigate(to);
        else navigate(to, options);
      });
    },
    [navigate, confirmLeave],
  ) as GuardedNavigate;
}

/** True while any registered guard has unsaved changes. */
export function useIsNavigationDirty(): boolean {
  return useContext(NavigationGuardContext)?.isAnyDirty ?? false;
}
